import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  padding: 3rem 1.5rem 4rem;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  box-shadow: 0 20px 35px -25px rgba(15, 23, 42, 0.35);
  padding: 2.5rem;
`;

const Header = styled.header`
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    color: #0f172a;
    margin-bottom: 0.5rem;
  }

  p {
    color: #64748b;
    font-size: 1rem;
  }
`;

const Section = styled.section`
  margin-bottom: 2rem;

  h2 {
    font-size: 1.25rem;
    color: #1e293b;
    margin-bottom: 0.75rem;
  }

  p {
    color: #475569;
    margin-bottom: 0.75rem;
    line-height: 1.7;
  }

  ul {
    padding-left: 1.5rem;
    color: #475569;
    line-height: 1.7;
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

const Footer = styled.footer`
  margin-top: 2.5rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  span {
    color: #64748b;
    font-size: 0.9rem;
  }
`;

const BackLink = styled(Link)`
  color: #2563eb;
  font-weight: 600;
`;

function TermsAndConditionsPage() {
  return (
    <Page>
      <Container>
        <Card>
          <Header>
            <h1>Terms and Conditions for BidLow</h1>
            <p>
              By using the BidLow USSD Bidding Service you agree to comply with these
              Terms and Conditions ("T&Cs"). Please read them carefully before participating.
            </p>
          </Header>

          <Section>
            <h2>1. Definitions</h2>
            <ul>
              <li><strong>"BidLow"</strong> - The USSD-based bidding platform.</li>
              <li><strong>"User"</strong> - Any person registered and using the BidLow service. Also known as a "Bidder".</li>
              <li><strong>"Bid"</strong> - An offer placed by a User to secure an auction at the lowest unique bid amount.</li>
              <li><strong>"Bid Wallet"</strong> - A virtual wallet where Users deposit funds for bidding Tickets.</li>
              <li><strong>"Ticket"</strong> - A paid entry required to participate in an auction.</li>
              <li><strong>"Lowest Unique Bid"</strong> - The successful bid is the lowest amount not duplicated within the last 5 minutes of the auction.</li>
            </ul>
          </Section>

          <Section>
            <h2>2. Eligibility &amp; Registration</h2>
            <ul>
              <li>Users must be 18+ years old and residents of Kenya.</li>
              <li>Registration requires a valid mobile number and M-Pesa integration.</li>
              <li>Only one account per mobile number is allowed.</li>
            </ul>
          </Section>

          <Section>
            <h2>3. Deposits &amp; Bidding Process</h2>
            <p><strong>3.1 Deposits via M-Pesa</strong></p>
            <ul>
              <li>Users can deposit any amount into their Bid Wallet.</li>
              <li>Each bid cost is deducted from the Bid Wallet in real time while bidding.</li>
            </ul>
            <p><strong>3.2 Bidding Rules</strong></p>
            <ul>
              <li>The lowest unique bid secures the auction.</li>
              <li>A bid must remain unique for 5 minutes to qualify as a successful bidder.</li>
              <li>If another User matches or undercuts the bid within 5 minutes, the bid loses uniqueness.</li>
              <li>Users can place multiple bids by purchasing multiple tickets.</li>
            </ul>
          </Section>

          <Section>
            <h2>4. Successful Bid &amp; Prize Fulfillment</h2>
            <ul>
              <li>Successful Bidders receive an SMS notification and must confirm delivery details.</li>
              <li>Prizes (e.g., phones, vouchers, airtime) will be delivered within 7 working days.</li>
              <li>BidLow reserves the right to substitute a prize of equal cash value if unavailable.</li>
              <li>Fraudulent bids (e.g., fake accounts, bots) will result in disqualification.</li>
            </ul>
          </Section>

          <Section>
            <h2>5. Fees &amp; Charges</h2>
            <ul>
              <li>Ticket Price is subject to change with notification given.</li>
              <li>M-Pesa transaction charges may apply on Deposits and Withdrawals.</li>
              <li>Taxes, where applicable, will be deducted and communicated before processing.</li>
            </ul>
          </Section>

          <Section>
            <h2>6. Refunds &amp; Withdrawals</h2>
            <ul>
              <li>No refunds for used bid tickets.</li>
              <li>Inactive accounts (6+ months) may be terminated.</li>
            </ul>
          </Section>

          <Section>
            <h2>7. Prohibited Activities</h2>
            <ul>
              <li>Bid manipulation (e.g., collusion, fake bids).</li>
              <li>Multiple accounts per user (leads to suspension).</li>
              <li>Chargeback disputes (may result in account ban).</li>
            </ul>
          </Section>

          <Section>
            <h2>8. Limitation of Liability</h2>
            <ul>
              <li>BidLow is not responsible for network failures, M-Pesa delays, or incorrect bids.</li>
              <li>Users participate at their own risk; BidLow does not guarantee successful bids.</li>
            </ul>
          </Section>

          <Section>
            <h2>9. Amendments &amp; Termination</h2>
            <ul>
              <li>BidLow may update T&amp;Cs with prior SMS notice.</li>
              <li>Users who violate terms may face account suspension.</li>
              <li>Disputes shall be resolved under Kenyan law.</li>
            </ul>
          </Section>

          <Footer />
        </Card>
      </Container>
    </Page>
  );
}

export default TermsAndConditionsPage;
